import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { MainHeader } from '../components/MainHeader';
import { Footer } from '../components/Footer';
import { PrimaryButton } from '../components/PrimaryButton';
import {
  customerApiErrorMessage,
  getMe,
  updateMe,
} from '../../lib/customer-api';
import { RequireCustomer } from '../../lib/use-customer-auth';
import { toast } from '../../lib/toast';

/**
 * Customer email notification preferences (/notifications). Mirrors the
 * professional page: persisted server-side on
 * `users.notification_preferences` via GET/PUT /customer/me and synced
 * to Klaviyo profile properties for flow/segment targeting. The master
 * "ads" toggle drives marketing-list membership. All categories
 * default off — the customer opts in.
 */
const PREF_DEFINITIONS: Array<{
  id: string;
  category: string;
  title: string;
  defaultEnabled: boolean;
}> = [
  {
    id: 'product-updates',
    category: 'Product updates',
    title: 'Receive messages from our platform',
    defaultEnabled: false,
  },
  {
    id: 'reminders',
    category: 'Reminders',
    title: 'Receive booking reminders and pricing notices',
    defaultEnabled: false,
  },
  {
    id: 'promotions',
    category: 'Promotions and tips',
    title: 'Receive coupons, promotions and surveys',
    defaultEnabled: false,
  },
  {
    id: 'policy',
    category: 'Policy and community',
    title: 'Receive updates on platform policies',
    defaultEnabled: false,
  },
  {
    id: 'account-support',
    category: 'Account support',
    title: 'Receive account, booking and legal alerts',
    defaultEnabled: false,
  },
  {
    id: 'ads',
    category: 'Ads and promotions',
    title: 'Unsubscribe from all marketing emails',
    defaultEnabled: false,
  },
];

// Merge stored prefs over the per-category defaults so a brand-new
// account (null in the DB) renders sensible toggles, and a partial
// stored map still fills any newly-added category.
function seedPrefs(
  stored: Record<string, boolean> | null | undefined,
): Record<string, boolean> {
  const seeded: Record<string, boolean> = {};
  for (const def of PREF_DEFINITIONS) {
    seeded[def.id] =
      stored && stored[def.id] !== undefined
        ? stored[def.id]
        : def.defaultEnabled;
  }
  return seeded;
}

export default function NotificationsPage() {
  return (
    <RequireCustomer>
      <NotificationsPageInner />
    </RequireCustomer>
  );
}

function NotificationsPageInner() {
  const navigate = useNavigate();

  const [prefs, setPrefs] = useState<Record<string, boolean>>(() =>
    seedPrefs(null),
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await getMe();
        if (cancelled) return;
        setPrefs(seedPrefs(res.info?.notification_preferences));
      } catch (err) {
        if (!cancelled)
          toast.error(
            customerApiErrorMessage(err, 'Could not load your preferences.'),
          );
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const toggle = (id: string) => {
    setPrefs((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await updateMe({ notification_preferences: prefs });
      toast.success('Preferences updated.');
    } catch (err) {
      toast.error(
        customerApiErrorMessage(err, 'Could not save your preferences.'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <MainHeader />

      <main className="flex-1 max-w-[1140px] mx-auto w-full px-4 py-12">
        {/* Back and Title */}
        <div className="flex items-start gap-4 mb-10">
          <Link
            to="/account-settings"
            className="mt-1 w-[40px] h-[40px] bg-[#3a3335]/[0.04] rounded-[10px] flex items-center justify-center hover:bg-[#3a3335]/[0.08] transition-colors shrink-0"
          >
            <span className="material-icons text-[#908e8e] text-[18px]">
              chevron_left
            </span>
          </Link>
          <div>
            <h1 className="font-['Playfair_Display',sans-serif] font-bold text-[#8e2a2f] text-[18px] mb-1">
              Notifications
            </h1>
            <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] tracking-[-0.1px]">
              Choose which emails you want to receive
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="flex flex-col lg:flex-row justify-between gap-12">
          {/* Left Column */}
          <div className="flex-1 max-w-[553px]">
            {loading ? (
              <div className="py-16 text-center text-[#908e8e] font-['DM_Sans',sans-serif] text-[14px]">
                Loading your preferences…
              </div>
            ) : (
              <div className="space-y-0 divide-y divide-[#eee]">
                {PREF_DEFINITIONS.map((def) => {
                  const enabled = !!prefs[def.id];
                  return (
                    <div
                      key={def.id}
                      className="py-6 flex items-start justify-between group"
                    >
                      <div className="flex-1 pr-6">
                        <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[12px] tracking-[-0.1px] mb-1">
                          {def.category}
                        </p>
                        <p
                          className={`font-['DM_Sans',sans-serif] font-bold text-[14px] tracking-[-0.1px] transition-colors ${
                            enabled ? 'text-[#3a3335]' : 'text-[#908e8e]'
                          }`}
                        >
                          {def.title}
                        </p>
                      </div>

                      <button
                        type="button"
                        onClick={() => toggle(def.id)}
                        aria-pressed={enabled}
                        aria-label={`Toggle ${def.category}`}
                        className={`relative w-[40px] h-[22px] rounded-full transition-colors duration-200 mt-2 shrink-0 ${
                          enabled ? 'bg-[#8e2a2f]' : 'bg-[#908e8e]/20'
                        }`}
                      >
                        <div
                          className={`absolute top-[2px] w-[18px] h-[18px] bg-white rounded-full shadow-sm transition-transform duration-200 ${
                            enabled ? 'translate-x-[20px]' : 'translate-x-[2px]'
                          }`}
                        />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Bottom Actions */}
            <div className="mt-12 flex items-center gap-4">
              <PrimaryButton
                className="w-auto px-6 h-[50px] text-[14px] whitespace-nowrap"
                onClick={handleSave}
                disabled={loading || saving}
              >
                {saving ? 'Saving…' : loading ? 'Loading…' : 'Update Settings'}
              </PrimaryButton>
              <button
                type="button"
                onClick={() => navigate('/account-settings')}
                className="px-8 h-[50px] rounded-[10px] font-['DM_Sans',sans-serif] font-bold text-[#908e8e] text-[14px] bg-[#3a3335]/[0.04] hover:bg-[#3a3335]/[0.08] transition-all"
              >
                Cancel
              </button>
            </div>
          </div>

          {/* Right Info Card */}
          <div className="w-full lg:w-[361px] shrink-0">
            <div className="bg-white rounded-[6px] shadow-[0px_3px_4px_0px_rgba(153,155,168,0.15)] p-8">
              <div className="w-[48px] h-[48px] rounded-[8px] bg-[#3a3335]/[0.04] flex items-center justify-center mb-6">
                <span className="material-icons text-[#908e8e] text-[22px]">
                  notifications
                </span>
              </div>
              <h3 className="font-['Inter',sans-serif] font-bold text-[#3a3335] text-[16px] leading-[24px] mb-3">
                Updates and alerts
              </h3>
              <p className="font-['DM_Sans',sans-serif] text-[#908e8e] text-[14px] leading-[21px] tracking-[-0.1px]">
                Toggle the categories you want to hear about. Critical account
                and booking alerts always come through regardless of these
                settings.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
